import React, { useEffect } from 'react';
import { Box, Grid, Stack, TextField } from '@mui/material';
import { FormikProvider, Form, useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, UpdateButton } from '../../components/mui/Buttons/Buttons';
import { editRefundBankDetails } from '../../redux/features/refundSlice';

const EditBankDetails = ({ open, setOpen, rowData, selectedType, onSuccess }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { submitionLoading } = useSelector((state) => state.refund);

  const Schema = Yup.object().shape({
    refund_ac_holder: Yup.string().required('Account holder name is required'),

    refund_ac_no: Yup.string().required('Account number is required'),

    refund_ac_bankname: Yup.string().required('Bank name is required'),

    refund_ifsc: Yup.string()
      .required('IFSC Code is required')
      .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC Code'),

    remarks: selectedType == 1 ? Yup.string().required('Remarks is required') : Yup.string(),
  });

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      refund_ac_holder: rowData?.refund_ac_holder || '',
      refund_ac_no: rowData?.refund_ac_no || '',
      refund_ac_bankname: rowData?.refund_ac_bankname || '',
      refund_ifsc: rowData?.refund_ifsc || '',
      remarks: '',
    },

    validationSchema: Schema,

    onSubmit: (values) => {
      const payload =
        selectedType == 1
          ? {
              holdamount_refund_id: rowData?.holdamount_refund_id,
              refund_ac_holder: values.refund_ac_holder,
              refund_ac_no: values.refund_ac_no,
              refund_ac_bankname: values.refund_ac_bankname,
              refund_ifsc: values.refund_ifsc,
              remarks: values.remarks,
            }
          : {
              expense_id: rowData?.expense_id,
              refund_ac_holder: values.refund_ac_holder,
              refund_ac_no: values.refund_ac_no,
              refund_ac_bankname: values.refund_ac_bankname,
              refund_ifsc: values.refund_ifsc,
            };

      dispatch(editRefundBankDetails(payload)).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, {
            variant: 'success',
          });

          setOpen(false);

          if (onSuccess) {
            onSuccess();
          }
        } else {
          enqueueSnackbar(res.payload?.message || 'Something went wrong', {
            variant: 'error',
          });
        }
      });
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  return (
    <GlobalDialog
      title="Edit Bank Details"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  name="refund_ac_holder"
                  value={formik.values.refund_ac_holder}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.refund_ac_holder && Boolean(formik.errors.refund_ac_holder)}
                  helperText={formik.touched.refund_ac_holder && formik.errors.refund_ac_holder}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Number"
                  name="refund_ac_no"
                  value={formik.values.refund_ac_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.refund_ac_no && Boolean(formik.errors.refund_ac_no)}
                  helperText={formik.touched.refund_ac_no && formik.errors.refund_ac_no}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  name="refund_ac_bankname"
                  value={formik.values.refund_ac_bankname}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.refund_ac_bankname && Boolean(formik.errors.refund_ac_bankname)}
                  helperText={formik.touched.refund_ac_bankname && formik.errors.refund_ac_bankname}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="refund_ifsc"
                  value={formik.values.refund_ifsc}
                  onChange={(e) => {
                    formik.setFieldValue('refund_ifsc', e.target.value.toUpperCase());
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.refund_ifsc && Boolean(formik.errors.refund_ifsc)}
                  helperText={formik.touched.refund_ifsc && formik.errors.refund_ifsc}
                />
              </Grid>

              {selectedType == 1 && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Remarks"
                    name="remarks"
                    value={formik.values.remarks}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                    helperText={formik.touched.remarks && formik.errors.remarks}
                  />
                </Grid>
              )}
            </Grid>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 3,
              }}
            >
              <Stack direction="row" spacing={1}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Cancel
                </CancelButton>

                <UpdateButton size="small" type="submit" disabled={submitionLoading}>
                  {submitionLoading ? 'Please wait...' : 'Update'}
                </UpdateButton>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
};

export default EditBankDetails;
